import { z } from "zod";
import type { ModelErrorType } from "../../shared/errors";
import {
  argumentsText,
  errorCode,
  finiteNumber,
  isRecord,
  jsonObject,
  jsonObjects,
  nonEmptyString,
  readableText,
  records,
} from "../../shared/json-body";
import { canonicalModelName } from "../../shared/models";
import { type RoutingFacts, skuById, skuIdOf } from "../../shared/pricing";
import { encryptedReasoningPart, toolPart } from "../../shared/prompt";
import type {
  DeclaredTool,
  ModelPrompt,
  ModelReply,
  ModelRequestSettings,
  ModelTransportDefinition,
  OutputContract,
  PromptPart,
  StreamChunk,
} from "../../shared/transport";
import type { ModelUsage } from "../../shared/usage";

/** The most output a request may ask for when Vertex's price table gives no limit. */
const MAX_OUTPUT_TOKENS = 65_536;

/** Vertex names the model and the method in the path, as Google's SDKs call it. */
const VERTEX_PATH =
  /^(v1|v1beta1)\/publishers\/google\/models\/([^/:]+):(generateContent|streamGenerateContent)$/;

const pathParts = (path: string) => {
  const match = VERTEX_PATH.exec(path);
  return match
    ? { model: match[2] ?? "", stream: match[3] === "streamGenerateContent" }
    : undefined;
};

const InlineData = z.strictObject({
  mimeType: z.string(),
  data: z.string(),
  displayName: z.string().optional(),
});

/** One part of a turn. Vertex tells parts apart by which field they set, not by a type field. */
const Part = z.strictObject({
  text: z.string().optional(),
  thought: z.boolean().optional(),
  thoughtSignature: z.string().optional(),
  inlineData: InlineData.optional(),
  fileData: z
    .strictObject({
      mimeType: z.string(),
      fileUri: z.url({ protocol: /^https$/ }),
      displayName: z.string().optional(),
    })
    .optional(),
  functionCall: z
    .strictObject({
      id: z.string().optional(),
      name: z.string(),
      args: z.record(z.string(), z.unknown()).optional(),
      partialArgs: z.array(z.looseObject({})).optional(),
      willContinue: z.boolean().optional(),
    })
    .optional(),
  functionResponse: z
    .strictObject({
      id: z.string().optional(),
      name: z.string(),
      response: z.record(z.string(), z.unknown()),
      parts: z.array(z.strictObject({ inlineData: InlineData.optional() })).optional(),
      willContinue: z.boolean().optional(),
      scheduling: z.enum(["SCHEDULING_UNSPECIFIED", "SILENT", "WHEN_IDLE", "INTERRUPT"]).optional(),
    })
    .optional(),
  videoMetadata: z
    .strictObject({
      startOffset: z.string().optional(),
      endOffset: z.string().optional(),
      fps: z.number().optional(),
    })
    .optional(),
  mediaResolution: z.strictObject({ level: z.string().optional() }).optional(),
});

const Content = z.strictObject({
  role: z.enum(["user", "model"]).optional(),
  parts: z.array(Part),
});

/** Functions the caller runs, and Google Search, which Vertex runs and Pax prices. */
const Tool = z.strictObject({
  functionDeclarations: z
    .array(
      z.strictObject({
        name: z.string(),
        description: z.string().optional(),
        parameters: z.record(z.string(), z.unknown()).optional(),
        parametersJsonSchema: z.unknown().optional(),
        response: z.record(z.string(), z.unknown()).optional(),
        responseJsonSchema: z.unknown().optional(),
        behavior: z.enum(["UNSPECIFIED", "BLOCKING", "NON_BLOCKING"]).optional(),
      }),
    )
    .optional(),
  googleSearch: z
    .strictObject({
      excludeDomains: z.array(z.string()).optional(),
      blockingConfidence: z.string().optional(),
    })
    .optional(),
});

const GenerationConfig = z.strictObject({
  maxOutputTokens: z.number().int().positive().meta({
    description: "Required. At most the model's own limit.",
    example: 8192,
  }),
  temperature: z.number().min(0).max(2).optional(),
  topP: z.number().min(0).max(1).optional(),
  topK: z.number().positive().optional(),
  candidateCount: z.number().int().positive().optional(),
  stopSequences: z.array(z.string()).optional(),
  presencePenalty: z.number().optional(),
  frequencyPenalty: z.number().optional(),
  seed: z.number().int().optional(),
  responseMimeType: z.enum(["text/plain", "application/json", "text/x.enum"]).optional(),
  responseSchema: z.record(z.string(), z.unknown()).optional(),
  responseJsonSchema: z.unknown().optional(),
  responseModalities: z.array(z.literal("TEXT")).optional(),
  responseLogprobs: z.boolean().optional(),
  logprobs: z.number().int().min(1).max(20).optional(),
  audioTimestamp: z.boolean().optional(),
  mediaResolution: z
    .enum([
      "MEDIA_RESOLUTION_UNSPECIFIED",
      "MEDIA_RESOLUTION_LOW",
      "MEDIA_RESOLUTION_MEDIUM",
      "MEDIA_RESOLUTION_HIGH",
    ])
    .optional(),
  thinkingConfig: z
    .strictObject({
      thinkingBudget: z.number().int().optional(),
      includeThoughts: z.boolean().optional(),
      thinkingLevel: z
        .enum(["THINKING_LEVEL_UNSPECIFIED", "MINIMAL", "LOW", "MEDIUM", "HIGH"])
        .optional(),
    })
    .optional(),
});

export const VertexGenerateContentRequest = z
  .strictObject({
    contents: z.array(Content),
    systemInstruction: Content.optional(),
    generationConfig: GenerationConfig,
    tools: z.array(Tool).optional(),
    toolConfig: z
      .strictObject({
        functionCallingConfig: z
          .strictObject({
            mode: z.enum(["MODE_UNSPECIFIED", "AUTO", "ANY", "NONE", "VALIDATED"]).optional(),
            allowedFunctionNames: z.array(z.string()).optional(),
            streamFunctionCallArguments: z.boolean().optional(),
          })
          .optional(),
        retrievalConfig: z
          .strictObject({
            latLng: z.strictObject({ latitude: z.number(), longitude: z.number() }).optional(),
            languageCode: z.string().optional(),
          })
          .optional(),
      })
      .optional(),
    safetySettings: z
      .array(
        z.strictObject({
          category: z.string(),
          threshold: z.string(),
          method: z.string().optional(),
        }),
      )
      .optional(),
    labels: z.record(z.string(), z.string()).optional(),
  })
  .meta({
    id: "VertexGenerateContentRequest",
    description:
      "A Vertex generateContent request. The model and whether it streams are in the path. Every documented field is accepted except `cachedContent`, which keeps state at Google, Model Armor, and output other than text. `generationConfig.maxOutputTokens` is required. Any other field is refused.",
    example: {
      contents: [{ role: "user", parts: [{ text: "Who holds Burgundy in 1444?" }] }],
      generationConfig: { maxOutputTokens: 8192 },
    },
  });
export type VertexGenerateContentRequest = z.infer<typeof VertexGenerateContentRequest>;

export const VertexModelParams = z.strictObject({
  model: z
    .string()
    .regex(/^[^/:]+$/)
    .meta({ description: "The Gemini model, named in the path.", example: "gemini-3.8-flash" }),
});

export const VertexGenerateContentResponse = z
  .looseObject({
    candidates: z.array(z.looseObject({})).optional(),
    usageMetadata: z.looseObject({}).optional(),
    modelVersion: z.string().optional(),
  })
  .meta({
    id: "VertexGenerateContentResponse",
    description:
      "Vertex's response, byte for byte: one response, or a stream of them from `streamGenerateContent`.",
  });

/**
 * Gemini on Vertex, called with a Vertex API key and priced from Vertex's published rates. API
 * keys reach only `generateContent` and `streamGenerateContent`.
 */
export const vertexGenerateContent: ModelTransportDefinition<VertexGenerateContentRequest> = {
  vendor: "vertex",
  provider: "gcp.vertex_ai",
  acceptsPath: path => VERTEX_PATH.test(path),
  upstreamUrl: path => `https://aiplatform.googleapis.com/${path}`,
  credential: "VERTEX_API_KEY",
  authorize: (headers, credential) => headers.set("x-goog-api-key", credential),
  request: VertexGenerateContentRequest,
  headers: {
    "content-type": z.string().regex(/^application\/json\b/),
    accept: z.string(),
    "x-vertex-ai-llm-shared-request-type": z.enum(["priority", "flex"]),
  },
  query: { alt: z.literal("sse") },
  billedBy: "vertex",
  readRequestedFacts: request =>
    vertexRequestedFacts(
      pathParts(request.path)?.model,
      request.headers.get("x-vertex-ai-llm-shared-request-type"),
    ),
  skuOf: vertexSkuOf,
  refusal: (path, request) => {
    const model = pathParts(path)?.model ?? "";
    const sku = skuById(vertexSkuOf(vertexRequestedFacts(model, null)));
    if (!sku) return { refusal: "model_not_found", message: `Pax has no price for ${model}.` };
    const limit = sku.maxOutputTokens ?? MAX_OUTPUT_TOKENS;
    const asked = request.generationConfig.maxOutputTokens;
    if (asked > limit)
      return {
        refusal: "invalid_request",
        message: `generationConfig.maxOutputTokens is ${asked}, above ${model}'s limit of ${limit}.`,
      };
    return null;
  },
  readModel: path => pathParts(path)?.model,
  readUsage,
  readSettings,
  readPrompt,
  readReply,
  readChunks,
  readOutputContract: (_path, requestBody) => readOutputContract(requestBody),
  errorTypeOf,
};

/**
 * Google's statuses. With an API key, `RESOURCE_EXHAUSTED` usually means Vertex's shared capacity
 * ran short, which is Google's doing; it is Pax's own quota only when the message names one.
 */
function errorTypeOf(code: string | undefined, message: string | undefined) {
  if (code === "RESOURCE_EXHAUSTED")
    return message && /quota exceeded/i.test(message) ? "rate_limited" : "overloaded";
  return code ? vertexErrorTypes[code] : undefined;
}

const vertexErrorTypes: Record<string, ModelErrorType> = {
  UNAVAILABLE: "overloaded",
  INTERNAL: "server_error",
  UNKNOWN: "server_error",
  DEADLINE_EXCEEDED: "timeout",
  INVALID_ARGUMENT: "invalid_request",
  FAILED_PRECONDITION: "invalid_request",
  UNAUTHENTICATED: "auth",
  PERMISSION_DENIED: "auth",
  NOT_FOUND: "model_not_found",
  CANCELLED: "cancelled",
};

/** Function declarations take JSON arguments; Google Search and code execution are Google's own. */
function readOutputContract(requestBody: string): OutputContract {
  const body = jsonObject(requestBody);
  const generation = isRecord(body.generationConfig) ? body.generationConfig : {};
  const tools = new Map<string, DeclaredTool>();
  let openTools = false;
  for (const tool of records(body.tools))
    for (const [kind, value] of Object.entries(tool)) {
      if (kind !== "functionDeclarations") {
        openTools = true;
        continue;
      }
      for (const declaration of records(value)) {
        const name = nonEmptyString(declaration.name);
        if (!name) continue;
        const schema =
          declaration.parametersJsonSchema ?? openApiToJsonSchema(declaration.parameters);
        tools.set(name, { json: true, schema });
      }
    }
  const json = generation.responseMimeType === "application/json";
  return {
    tools,
    openTools,
    json,
    responseSchema: json
      ? (generation.responseJsonSchema ?? openApiToJsonSchema(generation.responseSchema))
      : undefined,
  };
}

/** Vertex's OpenAPI schemas name types in capitals and mark nulls with `nullable`. */
function openApiToJsonSchema(schema: unknown): unknown {
  if (Array.isArray(schema)) return schema.map(openApiToJsonSchema);
  if (!isRecord(schema)) return schema;
  const converted: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(schema)) {
    if (key === "nullable") continue;
    converted[key] =
      key === "type" && typeof value === "string"
        ? value.toLowerCase()
        : openApiToJsonSchema(value);
  }
  if (schema.nullable === true && typeof converted.type === "string")
    converted.type = [converted.type, "null"];
  return converted;
}

/**
 * Every response object in a body: streamed events, one document, or the JSON array
 * `streamGenerateContent` returns when the request did not ask for server-sent events.
 */
function responses(body: string): Record<string, unknown>[] {
  const trimmed = body.trim();
  if (trimmed.startsWith("[")) {
    try {
      return records(JSON.parse(trimmed));
    } catch {}
  }
  return jsonObjects(body);
}

/**
 * The last usage a response reported. Gemini counts thinking apart from the reply and tool-use
 * prompts apart from the prompt; both are added back here. Vertex does not say whether cached
 * tokens came from a cache the request named or one it kept on its own, so all are counted as
 * the latter, which Vertex prices the same. Each Google Search query the model grounded on is
 * one use of `google_search`.
 */
function readUsage(body: string): ModelUsage | undefined {
  const events = responses(body);
  const last = [...events].reverse().find(event => isRecord(event.usageMetadata));
  if (!last || !isRecord(last.usageMetadata)) return;
  const usage = last.usageMetadata;
  const thinking = finiteNumber(usage.thoughtsTokenCount) ?? 0;
  const searchQueries = new Set(
    events.flatMap(event =>
      records(event.candidates).flatMap(candidate =>
        isRecord(candidate.groundingMetadata) &&
        Array.isArray(candidate.groundingMetadata.webSearchQueries)
          ? candidate.groundingMetadata.webSearchQueries
          : [],
      ),
    ),
  );
  return {
    model: nonEmptyString(last.modelVersion),
    inputTokens:
      (finiteNumber(usage.promptTokenCount) ?? 0) +
      (finiteNumber(usage.toolUsePromptTokenCount) ?? 0),
    audioInputTokens: records(usage.promptTokensDetails)
      .filter(detail => detail.modality === "AUDIO")
      .reduce((sum, detail) => sum + (finiteNumber(detail.tokenCount) ?? 0), 0),
    implicitCacheReadTokens: finiteNumber(usage.cachedContentTokenCount) ?? 0,
    explicitCacheReadTokens: 0,
    cacheWrites: [],
    outputTokens: (finiteNumber(usage.candidatesTokenCount) ?? 0) + thinking,
    reasoningTokens: thinking,
    toolUses: searchQueries.size ? { google_search: searchQueries.size } : {},
    serviceTier: nonEmptyString(usage.trafficType),
  };
}

function readSettings(path: string, requestBody: string): ModelRequestSettings {
  const body = jsonObject(requestBody);
  const generation = isRecord(body.generationConfig) ? body.generationConfig : {};
  const thinking = isRecord(generation.thinkingConfig) ? generation.thinkingConfig : {};
  const budget = finiteNumber(thinking.thinkingBudget);
  return {
    model: pathParts(path)?.model,
    maxTokens: finiteNumber(generation.maxOutputTokens),
    temperature: finiteNumber(generation.temperature),
    topP: finiteNumber(generation.topP),
    stream: pathParts(path)?.stream ?? false,
    reasoningLevel:
      nonEmptyString(thinking.thinkingLevel)?.toLowerCase() ??
      (budget === undefined ? undefined : `${budget} tokens`),
    outputType: generation.responseMimeType === "application/json" ? "json" : undefined,
  };
}

/**
 * The system instruction, the tools, then the contents in order. Google does not publish how
 * Gemini lays a prompt out, so this follows the request's own order.
 */
function readPrompt(requestBody: string): ModelPrompt {
  const body = jsonObject(requestBody);
  const instruction = isRecord(body.systemInstruction) ? body.systemInstruction : {};
  const system: PromptPart[] = records(instruction.parts).flatMap(part => {
    const text = nonEmptyString(part.text);
    return text ? [{ role: "system", label: "System", text, sent: text }] : [];
  });
  const tools = records(body.tools).flatMap(tool =>
    Object.entries(tool).flatMap(([kind, value]) =>
      kind === "functionDeclarations"
        ? records(value).map(toolPart)
        : [toolPart({ type: kind, ...(isRecord(value) ? value : {}) })],
    ),
  );
  const conversation: PromptPart[] = [];
  const toolResults = new Map<string, string>();
  for (const content of records(body.contents)) {
    const model = content.role === "model";
    for (const part of records(content.parts))
      conversation.push(...contentParts(part, model, toolResults));
  }
  return { parts: [...system, ...tools, ...conversation], toolResults };
}

function contentParts(
  part: Record<string, unknown>,
  model: boolean,
  toolResults: Map<string, string>,
): PromptPart[] {
  const parts: PromptPart[] = [];
  const signature = nonEmptyString(part.thoughtSignature);
  const text = nonEmptyString(part.text);
  if (part.thought === true) {
    parts.push(
      text
        ? { role: "assistant", label: "Reasoning", text, sent: text }
        : encryptedReasoningPart(signature ?? ""),
    );
    return parts;
  }
  if (signature) parts.push(encryptedReasoningPart(signature));
  if (isRecord(part.functionCall)) {
    const call = part.functionCall;
    parts.push({
      role: "assistant",
      label: `Calls ${nonEmptyString(call.name) ?? "a tool"}`,
      text: readableText(call.args),
      sent: argumentsText(call.args),
    });
  } else if (isRecord(part.functionResponse)) {
    const result = part.functionResponse;
    const output = argumentsText(result.response);
    const id = nonEmptyString(result.id) ?? nonEmptyString(result.name);
    if (id) toolResults.set(id, output);
    parts.push({ role: "tool", label: "Tool result", text: readableText(output), sent: output });
  } else if (text !== undefined) {
    parts.push(
      model
        ? { role: "assistant", label: "Assistant", text, sent: text }
        : { role: "user", label: "User", text, sent: text },
    );
  } else if (!signature) {
    parts.push({
      role: model ? "assistant" : "user",
      label: Object.keys(part)[0] ?? "part",
      text: JSON.stringify(part, null, 2),
      sent: JSON.stringify(part),
    });
  }
  return parts;
}

/** Reads a whole response or its streamed chunks, joining the first candidate's parts. */
function readReply(responseBody: string): ModelReply {
  const reply: ModelReply = {
    reasoning: "",
    reasoningEncryptedWithoutSummary: false,
    text: "",
    toolCalls: [],
    finishReasons: [],
    nativeFinishReasons: [],
  };
  let signed = false;
  let nativeFinish: string | undefined;
  for (const event of responses(responseBody)) {
    reply.id = nonEmptyString(event.responseId) ?? reply.id;
    reply.model = nonEmptyString(event.modelVersion) ?? reply.model;
    if (isRecord(event.error)) {
      reply.errorType = nonEmptyString(event.error.status) ?? errorCode(event.error);
      reply.errorMessage = nonEmptyString(event.error.message);
    }
    if (isRecord(event.usageMetadata))
      reply.serviceTier = nonEmptyString(event.usageMetadata.trafficType) ?? reply.serviceTier;
    const candidate = records(event.candidates)[0];
    if (!candidate) continue;
    nativeFinish = nonEmptyString(candidate.finishReason) ?? nativeFinish;
    const content = isRecord(candidate.content) ? candidate.content : {};
    for (const part of records(content.parts)) {
      signed ||= !!part.thoughtSignature;
      const text = nonEmptyString(part.text) ?? "";
      if (part.thought === true) reply.reasoning += text;
      else if (isRecord(part.functionCall))
        reply.toolCalls.push({
          id: nonEmptyString(part.functionCall.id),
          name: nonEmptyString(part.functionCall.name) ?? "a tool",
          arguments: part.functionCall.args,
        });
      else reply.text += text;
    }
  }
  reply.reasoningEncryptedWithoutSummary = signed && !reply.reasoning;
  if (nativeFinish) {
    reply.finishReasons = [finishReason(nativeFinish, reply.toolCalls.length > 0)];
    reply.nativeFinishReasons = [nativeFinish];
  }
  return reply;
}

function readChunks(responseBody: string): StreamChunk[] {
  let calls = 0;
  return responses(responseBody).map((event): StreamChunk => {
    const candidate = records(event.candidates)[0] ?? {};
    const content = isRecord(candidate.content) ? candidate.content : {};
    const parts = records(content.parts);
    const call = parts.find(part => isRecord(part.functionCall))?.functionCall;
    if (isRecord(call)) {
      calls += 1;
      return {
        kind: "tool",
        tool: nonEmptyString(call.name) ?? "a tool",
        call: String(calls),
        text: argumentsText(call.args),
      };
    }
    const thought = parts.filter(part => part.thought === true);
    if (thought.length)
      return { kind: "reasoning", text: thought.map(part => part.text ?? "").join("") };
    const text = parts.map(part => nonEmptyString(part.text) ?? "").join("");
    if (text) return { kind: "text", text };
    if (nonEmptyString(candidate.finishReason)) return { kind: "finish" };
    if (isRecord(event.usageMetadata)) return { kind: "usage" };
    return { kind: "other" };
  });
}

const FILTERED = new Set(["SAFETY", "RECITATION", "BLOCKLIST", "PROHIBITED_CONTENT", "SPII"]);

function finishReason(reason: string, calledTools: boolean): string {
  if (reason === "STOP") return calledTools ? "tool_call" : "stop";
  if (reason === "MAX_TOKENS") return "length";
  if (FILTERED.has(reason)) return "content_filter";
  return reason.toLowerCase();
}

/** The traffic type each value of the shared request type header asks Vertex for. */
const tierOfRequestType: Readonly<Record<string, string>> = {
  priority: "ON_DEMAND_PRIORITY",
  flex: "ON_DEMAND_FLEX",
};

/** What a Vertex request asked for: on-demand, unless the request type header asks otherwise. */
function vertexRequestedFacts(
  model: string | undefined,
  requestType: string | null,
): Partial<RoutingFacts> {
  return {
    ...(model === undefined ? {} : { model }),
    tier: (requestType && tierOfRequestType[requestType]) ?? "ON_DEMAND",
  };
}

function vertexSkuOf(requested: Partial<RoutingFacts>): string | undefined {
  const { model, tier } = requested;
  if (model === undefined || tier === undefined) return;
  return skuIdOf({
    billedBy: "vertex",
    model: canonicalModelName(model),
    endpoint: "global",
    tier,
  });
}
